import React, { useMemo } from 'react';

import { HStack, Icon } from '@rbilabs/universal-components';
import { Typography } from 'ucl/components/text/Typography';

import { IBaseItem } from '@rbi-ctg/menu';
import { Calories } from 'components/price-and-calories';
import useEffectOnce from 'hooks/use-effect-once';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useFormatCalories } from 'hooks/use-format-calories';
import { CustomEventNames, logRBIProductsDetailsEvent } from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { t } from 'state/intl/translations';
import { useMenuContext } from 'state/menu';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { useUIContext } from 'state/ui';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { primitive } from 'styles/constants';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';

import {
  UpsellItemButton,
  UpsellItemCalories,
  UpsellItemDetails,
  UpsellItemName,
  UpsellItemPicture,
  UpsellItemPrice,
} from './styles';
import { IUpsellItemProps } from './types';

const useUpsellPrice = (item: IBaseItem) => {
  const { pricingFunction } = useMenuContext();
  const { posServiceMode } = useDerivedServiceModeContext();
  const { indexedMenu } = useUnifiedMenuContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  if (isUnifiedMenuEnabled) {
    const menuItem = indexedMenu.find(item._id);
    if (menuItem) {
      return menuItem.price?.[posServiceMode];
    }
    return;
  }
  return pricingFunction({ item });
};

const UpsellItem: React.FC<React.PropsWithChildren<IUpsellItemProps>> = ({ item, addToCard }) => {
  const formatCalories = useFormatCalories();
  const { formatCurrencyForLocale } = useUIContext();
  const itemCalories = item.nutritionWithModifiers?.calories;
  const { isOrderPriceLoading } = useCartContext();
  const upsellPrice = useUpsellPrice(item) ?? 0;

  const price = formatCurrencyForLocale(upsellPrice);
  const calories = useMemo(() => formatCalories(itemCalories), [formatCalories, itemCalories]);

  useEffectOnce(() => {
    logRBIProductsDetailsEvent({
      name: CustomEventNames.UPSELL_DISPLAYED,
      products: [item],
      price: upsellPrice / 100,
    });
  });

  const addItemLabel = t('addItemLabel', { itemName: item.name?.locale });
  const upsellPriceAndCaloriesLabel = `${price}. ${calories}.`;
  const upsellItemLabel = `${addItemLabel}. ${upsellPriceAndCaloriesLabel}.`;

  return (
    <UpsellItemButton
      disabled={isOrderPriceLoading}
      _web={{ cursor: isOrderPriceLoading ? 'not-allowed' : 'pointer' }}
      testID="upsell-add-to-order-btn"
      accessibilityLabel={upsellItemLabel}
      accessibilityRole="button"
      onPress={() => {
        if (isOrderPriceLoading) {
          return;
        }

        hapticImpact({ style: HapticsImpactStyle.Light });
        addToCard(item);
      }}
      borderWidth={0}
    >
      <UpsellItemPicture image={item.image} alt={item.name?.locale} />
      <UpsellItemDetails accessibilityLabel={upsellPriceAndCaloriesLabel}>
        <UpsellItemName
          style={{ userSelect: 'none' }}
          testID={`upsell-name-${item._id}`}
          accessibilityLevel={3}
        >
          {item.name?.locale}
        </UpsellItemName>
        <HStack>
          <UpsellItemPrice testID={`upsell-price-${item._id}`}>
            <Typography style={{ userSelect: 'none' }} testID={`upsell-price-text-${item._id}`}>
              {price}
            </Typography>
          </UpsellItemPrice>
          <Calories>
            <Typography style={{ userSelect: 'none' }} variant="copyOne">
              •
            </Typography>
            <UpsellItemCalories
              style={{ userSelect: 'none' }}
              testID={`upsell-calories-${item._id}`}
            >
              {calories}
            </UpsellItemCalories>
          </Calories>
        </HStack>
      </UpsellItemDetails>
      <Icon
        variant="bkUpsellPlusV2"
        title={upsellItemLabel}
        accessibilityRole="button"
        accessibilityLabel={upsellItemLabel}
        color={isOrderPriceLoading ? primitive.$blackOpacity50 : primitive.$bbqBrown}
        size="30"
        marginLeft="$2"
      />
    </UpsellItemButton>
  );
};

export default UpsellItem;
